// src/components/ModalNovaVendaBalcao.jsx
import React, { useState, useMemo, useEffect } from 'react';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

import { Search, User, UserPlus, Plus, Trash2 } from 'lucide-react';
import ModalClienteRapido from './ModalClienteRapido';

const ModalNovaVendaBalcao = ({
  open,
  onClose,
  onSave,
  form,
  setForm,
  produtosDisponiveis = [],
  clientesDisponiveis = [],
}) => {
  const [buscaCliente, setBuscaCliente] = useState('');
  const [mostrarResultados, setMostrarResultados] = useState(false);

  const [produtoSelecionadoId, setProdutoSelecionadoId] = useState('');
  const [quantidadeProduto, setQuantidadeProduto] = useState(1);

  const [clienteRapidoOpen, setClienteRapidoOpen] = useState(false);

  const clientesFiltrados = useMemo(() => {
    if (!buscaCliente || buscaCliente.trim().length < 2) return [];
    const termo = buscaCliente.toLowerCase();
    return (clientesDisponiveis || []).filter(c =>
      (c?.nome || '').toLowerCase().includes(termo)
    );
  }, [buscaCliente, clientesDisponiveis]);

  useEffect(() => {
    const total = (form.produtos || []).reduce((acc, p) => {
      const preco = Number(p.precoVenda) || 0;
      const qtd = Number(p.quantidade) || 0;
      return acc + preco * qtd;
    }, 0);
    setForm(prev => ({ ...prev, valorTotal: total }));
  }, [form.produtos, setForm]);

  if (!open) return null;

  const handleSelecionarCliente = (cliente) => {
    setForm(prev => ({
      ...prev,
      clienteId: cliente.id,
      clienteNome: cliente.nome || cliente.razaoSocial || '',
      clienteNomeAvulso: ''
    }));
    setBuscaCliente('');
    setMostrarResultados(false);
  };

  const handleClienteRapidoSaved = (clienteCriado) => {
    handleSelecionarCliente({
      id: clienteCriado.id,
      nome: clienteCriado.nome || clienteCriado.razaoSocial,
    });
    setClienteRapidoOpen(false);
  };

  const handleLimparCliente = () => {
    setForm(prev => ({ ...prev, clienteId: '', clienteNome: '', clienteNomeAvulso: '' }));
  };

  const handleAdicionarProduto = () => {
    const qtd = Number(quantidadeProduto);
    if (!produtoSelecionadoId || !Number.isFinite(qtd) || qtd <= 0) {
      Swal.fire('Atenção', 'Selecione um produto e informe uma quantidade válida.', 'warning');
      return;
    }
    const prod = (produtosDisponiveis || []).find(p => p.id === produtoSelecionadoId);
    if (!prod) return;

    const idxExistente = (form.produtos || []).findIndex(p => p.id === prod.id);
    if (idxExistente > -1) {
      const novos = [...form.produtos];
      novos[idxExistente].quantidade = Number(novos[idxExistente].quantidade || 0) + qtd;
      setForm(prev => ({ ...prev, produtos: novos }));
    } else {
      setForm(prev => ({
        ...prev,
        produtos: [
          ...(prev.produtos || []),
          { ...prod, quantidade: qtd }
        ]
      }));
    }

    setProdutoSelecionadoId('');
    setQuantidadeProduto(1);
  };

  const handleRemoverProduto = (index) => {
    setForm(prev => ({ ...prev, produtos: (prev.produtos || []).filter((_, i) => i !== index) }));
  };

  const handleSalvar = () => {
    if (!form.produtos || form.produtos.length === 0) {
      Swal.fire('Atenção', 'Adicione pelo menos um produto à venda.', 'warning');
      return;
    }
    onSave(form);
  };

  const selectBase =
    'flex-1 px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-800 dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD] transition';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-fadein">
      <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-4xl relative animate-modalSlideIn overflow-hidden">
        <div className="max-h-[94vh] flex flex-col">
          {/* Header */}
          <div className="p-7 pb-0 relative">
            <button
              className="absolute right-8 top-7 text-gray-500 hover:text-blue-700 dark:text-gray-200 dark:hover:text-white text-base font-medium"
              onClick={onClose}
            >
              Fechar
            </button>
            <h2 className="text-2xl font-bold mb-6 text-blue-600 dark:text-white">
              Nova Venda
            </h2>
          </div>

          {/* Body */}
          <div className="px-7 pb-6 space-y-6 overflow-y-auto flex-1">
            {/* Cliente */}
            <div>
              <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Cliente</label>

              {form.clienteId ? (
                <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/40 px-4 py-3 rounded-xl">
                  <div className="flex items-center gap-2">
                    <User size={16} className="text-blue-700 dark:text-blue-300" />
                    <span className="font-semibold text-blue-900 dark:text-blue-200">
                      {form.clienteNome}
                    </span>
                  </div>
                  <button
                    onClick={handleLimparCliente}
                    className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-semibold text-sm"
                  >
                    Alterar
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="relative">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={buscaCliente}
                      onChange={e => setBuscaCliente(e.target.value)}
                      onFocus={() => setMostrarResultados(true)}
                      onBlur={() => setTimeout(() => setMostrarResultados(false), 200)}
                      placeholder="Buscar cliente cadastrado..."
                      className="w-full pl-10 pr-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD] transition"
                    />

                    {mostrarResultados && clientesFiltrados.length > 0 && (
                      <ul className="absolute z-10 w-full bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-xl mt-1 max-h-56 overflow-y-auto shadow-lg">
                        {clientesFiltrados.map(cliente => (
                          <li
                            key={cliente.id}
                            className="px-4 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/40 cursor-pointer rounded-lg"
                            onClick={() => handleSelecionarCliente(cliente)}
                          >
                            <p className="font-semibold text-gray-800 dark:text-gray-100">{cliente.nome}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{cliente.cpf || cliente.cnpj}</p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={form.clienteNomeAvulso || ''}
                      onChange={e => setForm(prev => ({ ...prev, clienteNomeAvulso: e.target.value }))}
                      placeholder="Ou digite um nome (opcional)"
                      className="flex-1 px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD] transition"
                    />
                    {/* Ícone: Cadastro rápido */}
                    <button
                      type="button"
                      onClick={() => setClienteRapidoOpen(true)}
                      title="Cadastro rápido"
                      className="h-12 w-12 flex items-center justify-center rounded-full border border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/30"
                    >
                      <span className="sr-only">Cadastro rápido</span>
                      <UserPlus size={20} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Produtos */}
            <div>
              <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Produtos</label>

              <div className="space-y-2">
                {(form.produtos || []).map((produto, index) => {
                  const preco = Number(produto.precoVenda) || 0;
                  const qtd = Number(produto.quantidade) || 0;
                  const subtotal = preco * qtd;
                  return (
                    <div key={`${produto.id}-${index}`} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/70 px-4 py-3 rounded-xl">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">{produto.nome}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{qtd} × R$ {preco.toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="font-bold text-gray-900 dark:text-gray-100">R$ {subtotal.toFixed(2)}</p>
                        <button
                          onClick={() => handleRemoverProduto(index)}
                          className="p-1.5 text-red-600 hover:text-red-700 rounded-md"
                          title="Remover"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {(!form.produtos || form.produtos.length === 0) && (
                <div className="text-center py-6 border-2 border-dashed rounded-xl text-gray-500 dark:text-gray-400 dark:border-gray-700 mt-2">
                  Nenhum produto adicionado.
                </div>
              )}

              <div className="flex items-center gap-3 mt-4">
                {/* Select com placeholder cinza e oculto no dropdown */}
                <select
                  value={produtoSelecionadoId}
                  onChange={e => setProdutoSelecionadoId(e.target.value)}
                  className={`${selectBase} ${!produtoSelecionadoId ? 'text-gray-400' : 'text-gray-900 dark:text-white'}`}
                >
                  <option value="" disabled hidden>Selecione um produto</option>
                  {(produtosDisponiveis || []).map(p => (
                    <option key={p.id} value={p.id}>
                      {p.nome} — R$ {(Number(p.precoVenda) || 0).toFixed(2)}
                    </option>
                  ))}
                </select>

                <input
                  type="number"
                  min="1"
                  value={quantidadeProduto}
                  onChange={e => setQuantidadeProduto(Math.max(1, Number(e.target.value || 1)))}
                  className="w-28 px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD] transition"
                  placeholder="Qtd"
                />

                {/* Ícone: Adicionar produto */}
                <button
                  onClick={handleAdicionarProduto}
                  title="Adicionar produto"
                  className="h-12 w-12 flex items-center justify-center rounded-full border border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/30"
                >
                  <span className="sr-only">Adicionar produto</span>
                  <Plus size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-7 py-4 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-t dark:border-gray-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-b-3xl">
            <div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Valor Total</span>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                R$ {(Number(form.valorTotal) || 0).toFixed(2)}
              </p>
            </div>
            <div className="flex w-full sm:w-auto">
              <button
                onClick={handleSalvar}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-3 font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                <Plus size={18} /> Salvar Venda
              </button>
            </div>
          </div>
        </div>

        <style>{`
          .animate-modalSlideIn { animation: modalSlideIn 0.35s cubic-bezier(.32,.72,.57,1) both; }
          @keyframes modalSlideIn {
            0% { opacity:0; transform: translateY(60px) scale(.97); }
            100% { opacity:1; transform: translateY(0) scale(1); }
          }
          .animate-fadein { animation: fadein 0.25s; }
          @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
      </div>

      {/* SUB-MODAL: Cadastro rápido */}
      <ModalClienteRapido
        open={clienteRapidoOpen}
        onClose={() => setClienteRapidoOpen(false)}
        onSaved={handleClienteRapidoSaved}
      />
    </div>
  );
};

export default ModalNovaVendaBalcao;
